import { pasteContentExperience } from '@databases/frontend-metrics';
import { ParsedImportFile } from '@databases/import-processor/types';
import type { CreateMentionNotificationType } from '@databases/mention-notification/types';
import { DefaultRootState } from '@databases/redux';
import {
  deselectAll,
  selectValueRange,
  selectValueSingle,
  setSoftSelection,
} from '@databases/redux/cellSelections/actions';
import {
  selectedFieldsMatchType,
  selectHasSelectedCells,
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { setDraftedChangeset } from '@databases/redux/changesets/actions';
import {
  selectDatabaseDraftByContentId,
  selectDraftedChangeset,
} from '@databases/redux/changesets/selectors';
import {
  Database,
  DatabaseField,
  DatabaseFieldType,
  DatabaseValue,
  SelectOption,
} from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  addChangeToChangeset,
  DatabaseChange,
  DatabaseChangeset,
  DatabaseDraft,
  MultiValueChangeType,
  ValueChange,
} from '@databases/utils/database/changesets';
import {
  convertType,
  getInitialValue,
  isEmptyValue,
  isEqualValue,
} from '@databases/utils/database/field-types';
import { getNewOption } from '@databases/utils/database/field-types/select';
import {
  createEntry,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import {
  applyDatabaseView,
  getVisibleFields,
  matchesFilter,
} from '@databases/utils/database/view/view';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch, SimpleStore } from '@databases/utils/redux/redux';
import { deleteFromArray } from '@databases/utils/utils';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { renderPasteFlags } from '../helpers/messageHelper';
import {
  applyDeltaAfterPaste,
  convertToHtmlFormat,
  getImageItems,
  getImportSettingsFromSelection,
  getImportSettingsWithMissingItems,
  getLastEntryIdByChangeType,
  getLastFieldIdByChangeType,
  getNewSetValuesChangesets,
  handleApplyImportSettingsError,
  handleParsePasteError,
  handleReadPasteError,
  isUserArray,
  resolveEntryReference,
  resolveFieldReference,
} from '../helpers/pasteHelper';
import { applyImportSettings } from '../processes/applySetting';
import { parsePasteContent } from '../processes/parsePaste';
import { readPasteContent } from '../processes/readPaste';
import {
  ImportEntryMode,
  ImportEntryReference,
  ImportFieldMode,
  ImportResult,
  ImportSettings,
  ImportValues,
  ShowPasteFlag,
} from '../types';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

/**
 * Returns the database changes mapped from the value changes of the pasted content.
 * Converts the pasted cell text value to the new field type if the cell value type is different from the field type.
 **/
export function getValueChanges(
  cell: ImportValues,
  settings: ImportSettings,
  entryIdx: number,
  fieldIdx: number,
  database: DatabaseDraft,
  store: SimpleStore<DefaultRootState>
): DatabaseChange[] {
  const entryRef = settings.entryReferences[entryIdx];
  const entry = resolveEntryReference(database, entryRef);
  const fieldRef = settings.fieldReferences[fieldIdx];
  const field = resolveFieldReference(database, fieldRef);

  if (!field || !entry) {
    return [];
  }

  const convertedValue =
    cell?.text && cell.value?.type !== field.type
      ? convertType(
          {
            database,
            field,
            value: {
              value: cell.text,
              type: DatabaseFieldType.TEXT,
            },
          },
          field.type,
          store.getState()
        )
      : cell.value!;

  const valueChange = applyDeltaAfterPaste(
    {
      database,
      field,
      entry,
      value: convertedValue,
      text: cell.text,
    },
    store
  );

  return (valueChange || []).map((change: ValueChange) => ({
    type: DatabaseChangeType.SET_VALUE,
    entryId: entry.id,
    value: convertedValue,
    fieldId: field.id,
    change,
  }));
}

/**
 * Returns the database change for adding a new entry with the pasted content as text values
 * */
const handleAddEntryChange = (
  entryIdx: number,
  databaseDraft: DatabaseDraft,
  settings: ImportSettings,
  importResult: ImportResult,
  entryRef: ImportEntryReference
): DatabaseChange => {
  const entry = resolveEntryReference(databaseDraft, entryRef);
  return {
    type: DatabaseChangeType.ADD_ENTRY,
    entry: {
      ...entry,
      values: Object.fromEntries(
        settings.fieldReferences
          .map((fieldRef, idx) => {
            const field = resolveFieldReference(databaseDraft, fieldRef);
            const cell = importResult.rows?.[entryIdx]?.cols?.[idx];
            const value = cell?.value || {
              value: cell?.text || getInitialValue(DatabaseFieldType.TEXT).value,
              type: DatabaseFieldType.TEXT,
            };
            return field?.id && value ? [field.id, value] : [];
          })
          .filter(([fieldId, value]) => fieldId && value)
      ),
    },
  } as DatabaseChange;
};

/**
 *  Iterates over the fields and creates a change for replacing the value of the selected cells with the pasted content.
 * */
const handleFieldChanges = (
  entryIdx: number,
  databaseDraft: DatabaseDraft,
  settings: ImportSettings,
  importResult: ImportResult,
  store: SimpleStore<DefaultRootState>,
  currentViewId: string | undefined
): DatabaseChange[] => {
  let changes: DatabaseChange[] = [];
  settings.fieldReferences.forEach((fieldRef, fieldIdx) => {
    const field = resolveFieldReference(databaseDraft, fieldRef);
    const shouldCreateField = fieldRef?.type === ImportFieldMode.CREATE;
    const cell = importResult.rows?.[entryIdx]?.cols?.[fieldIdx];
    if (cell === undefined) {
      return;
    }

    if (shouldCreateField && field) {
      const createChange: DatabaseChange = {
        type: DatabaseChangeType.ADD_FIELD,
        field,
        currentViewId,
      };
      changes.push(createChange);
    }
    const fieldChanges = getValueChanges(
      cell,
      settings,
      entryIdx,
      fieldIdx,
      databaseDraft,
      store
    );
    changes = [...changes, ...fieldChanges];
  });
  return changes;
};

const getNewTags = (selectOptions: SelectOption[], tags: string[]) => {
  const selectOptionIds = selectOptions.map((option) => option.id ?? option.label);
  return tags.filter((string) => {
    return !selectOptionIds.includes(string);
  });
};

interface TagCreationData {
  newTags: Set<string>;
  cellIndicesWithNewTags: number[];
}

/** For each cell in importValues, determines if the cell has any new tags.
 * Returns a mapping of fieldIds to new tags to create + cells to replace with
 * new tag IDs for that field. */
const getTagsToCreate = (
  importValues: ImportValues[],
  database: Database
): Record<string, TagCreationData> => {
  const tagsToCreate: Record<string, TagCreationData> = {};

  importValues.forEach((importValue, index) => {
    const field = getDatabaseFieldOptional(database, importValue.targetFieldId);

    if (field && field.type === DatabaseFieldType.SELECT && importValue.value?.value) {
      const newTags = getNewTags(
        field.settings.selectOptions,
        (importValue.value as DatabaseValue<DatabaseFieldType.SELECT>)?.value
      );

      if (newTags.length > 0) {
        if (tagsToCreate[field.id]) {
          newTags.forEach((tag) => tagsToCreate[field.id].newTags.add(tag));
          tagsToCreate[field.id].cellIndicesWithNewTags.push(index);
        } else {
          tagsToCreate[field.id] = {
            newTags: new Set(newTags),
            cellIndicesWithNewTags: [index],
          };
        }
      }
    }
  });

  return tagsToCreate;
};

const createNewTagChange = (
  field: DatabaseField,
  label: string
): {
  newTagOption: SelectOption;
  change: DatabaseChange;
} => {
  const newTagOption = { ...getNewOption(field.settings.selectOptions), label: label };

  const change = {
    type: DatabaseChangeType.SET_SELECT_OPTIONS,
    fieldId: field.id,
    newValue: [...field.settings.selectOptions, newTagOption],
    change: {
      type: MultiValueChangeType.APPEND,
      value: newTagOption,
    },
  } as DatabaseChange;

  return { newTagOption, change };
};

const replaceTagLabelsWithIds = (
  cell: ImportValues,
  labelsToIds: Record<string, string>
): void => {
  const value = cell.value as DatabaseValue<DatabaseFieldType.SELECT>;
  const tags = value.value;
  const updatedTags = tags.map((label) => labelsToIds[label] || label);
  value.value = updatedTags;
};

/** For each fieldId, generates an ID and color for each new tag. Now that we
 have an ID for the new tags, updates the cells in importValues to use the ID
 instead of the label for new tags. Returns a changeset of changes to create new tags as well
 as changes to turn on "Allow tag creation" in field settings.  */
const getCreateNewTagChanges = (
  tagsToCreate: Record<string, TagCreationData>,
  database: Database,
  importValues: ImportValues[]
): {
  newTagOptionChanges: DatabaseChange[];
  allowTagCreationChanges: DatabaseChange[];
} => {
  const newTagOptionChanges: DatabaseChange[] = [];
  const allowTagCreationChanges: DatabaseChange[] = [];

  for (const fieldId in tagsToCreate) {
    const { newTags, cellIndicesWithNewTags } = tagsToCreate[fieldId];
    const field = getDatabaseFieldOptional(database, fieldId);
    if (!field) {
      continue;
    }
    const newTagsToIds: Record<string, string> = {};

    for (const tag of newTags) {
      const { newTagOption, change } = createNewTagChange(field, tag);
      newTagOptionChanges.push(change);
      newTagsToIds[tag] = newTagOption.id ?? newTagOption.label;
    }

    if (newTags.size > 0 && !field.settings.creatable) {
      const change = {
        type: DatabaseChangeType.SET_IS_CREATABLE,
        fieldId,
        isCreatable: true,
      } as DatabaseChange;
      allowTagCreationChanges.push(change);
    }

    for (const index of cellIndicesWithNewTags) {
      replaceTagLabelsWithIds(importValues[index], newTagsToIds);
    }
  }

  return {
    newTagOptionChanges,
    allowTagCreationChanges,
  };
};

/**
 * Iterates over the entries and creates a change for replacing the value of the selected cells with the pasted content.
 * If the pasted content area is larger than the selected area, the remaining cells will be filled with the pasted content.
 * If the selected area is larger than the pasted content, the extra cells will be unchanged.
 * */
export const createRangedChanges = (
  settings: ImportSettings,
  importResult: ImportResult,
  databaseDraft: DatabaseDraft,
  store: SimpleStore<DefaultRootState>,
  currentViewId: string | undefined
) => {
  let changes: DatabaseChange[] = [];

  settings.entryReferences.forEach((entryRef, entryIdx) => {
    if (entryRef?.type === ImportEntryMode.CREATE) {
      const addEntryChange = handleAddEntryChange(
        entryIdx,
        databaseDraft,
        settings,
        importResult,
        entryRef
      );
      changes = [...changes, addEntryChange];
    } else {
      const fieldChanges = handleFieldChanges(
        entryIdx,
        databaseDraft,
        settings,
        importResult,
        store,
        currentViewId
      );
      changes = [...changes, ...fieldChanges];
    }
  });

  return changes;
};

/**
 * Determines whether the multi setting should be turned on based on the values pasted
 **/
const shouldUpdateMultiSetting = (importValues: ImportValues[], database: Database) => {
  return importValues.some((importValue) => {
    const field = getDatabaseFieldOptional(database, importValue.targetFieldId);
    const isPastingIntoAttachmentField = field?.type === DatabaseFieldType.ATTACHMENT;

    return (
      !isPastingIntoAttachmentField && importValue.targetIsMulti && !field?.settings.multi
    );
  });
};

/**
 * Determines whether the pasted content is overwriting existing content. If the content is the same,
 * it is not considered as overwriting and this function returns false.
 **/
const isPastingOverExistingContent = (
  databaseChangeset: DatabaseChangeset,
  database: Database,
  state: DefaultRootState
) => {
  return databaseChangeset.some((changeset) => {
    if (changeset.type === DatabaseChangeType.SET_VALUE) {
      const field = getDatabaseFieldOptional(database, changeset.fieldId);
      const entry = getDatabaseEntryOptional(database, changeset.entryId);
      if (field && entry) {
        const sourceValue = changeset.value;
        const destinationValue = getDatabaseValue(database, field, entry);
        const destinationValueIsEmpty = isEmptyValue(
          { database, field, entry, value: destinationValue },
          state
        );
        const valuesAreEqual = isEqualValue(
          { database, field, value: sourceValue },
          { database, field, entry, value: destinationValue },
          state
        );
        return !destinationValueIsEmpty && !valuesAreEqual;
      }

      return false;
    }
  });
};

/**
 * Determines whether the pasted content matches the field type
 */
const doesPasteContentTypeMatchField = (
  importValues: ImportValues[],
  database: Database,
  changeset: DatabaseChangeset,
  state: DefaultRootState
) => {
  let isPastingContentThatDoesNotMatch = false;

  for (const importValue of importValues) {
    const field = getDatabaseFieldOptional(database, importValue.targetFieldId);

    const convertedPastedValue = changeset
      .filter((change) => change.type === DatabaseChangeType.SET_VALUE)
      .find(
        (change) =>
          change.fieldId === importValue.targetFieldId &&
          change.entryId === importValue.targetEntryId
      )?.value;

    if (importValue.targetEntryId) {
      const entry = getDatabaseEntryOptional(database, importValue.targetEntryId);
      // this happens when pasting content off the database. we know that we can return true because:
      // - all previous fields were matching
      // - the created field is guaranteed to match
      if (importValue.targetEntryId && !importValue.targetFieldId) {
        isPastingContentThatDoesNotMatch = false;
        break;
      } else if (convertedPastedValue === undefined) {
        isPastingContentThatDoesNotMatch = true;
        break;
      }

      if (field && entry) {
        const convertedPastedValueIsEmpty = isEmptyValue(
          { database, field, entry, value: convertedPastedValue },
          state
        );

        if (!importValue.isSourceEmpty && convertedPastedValueIsEmpty) {
          isPastingContentThatDoesNotMatch = true;
          break;
        }
      }
    }
  }

  return !isPastingContentThatDoesNotMatch;
};

type Mention = {
  mentionLocalId: string;
  mentionedUserAccountId: string;
  displayName: string;
};

// TODO: to be refactored in NCTD-3034
const createMentionsNotificationOnPaste = async (
  importValues: ImportValues[],
  database: Database,
  createMentionNotification: CreateMentionNotificationType
) => {
  const mentions: Mention[] = [];
  let fieldId;

  for (const importValue of importValues) {
    const field = getDatabaseFieldOptional(database, importValue.targetFieldId);

    if (
      field &&
      field.type === DatabaseFieldType.USER &&
      field.settings.mentionNotificationEnabled
    ) {
      fieldId = field.id;
      const users = importValue?.value?.value;

      const displayNames = importValue.text.split(',');

      if (isUserArray(users)) {
        users.forEach((user, index) => {
          mentions.push({
            mentionedUserAccountId: user.accountId,
            mentionLocalId: user._id,
            displayName: displayNames[index],
          });
        });
      }
    }
  }

  if (mentions.length && fieldId) {
    await createMentionNotification({
      mentions,
      fieldId,
    });
  }
};

/**
 * Determines the set of fields that needs the field setting "multi" to be set to true
 */
const getFieldSettingMultiChanges = (
  importValues: ImportValues[],
  database: Database
) => {
  const fieldSettingMultiChanges: DatabaseChange[] = [];

  for (const importValue of importValues) {
    const field = getDatabaseFieldOptional(database, importValue.targetFieldId);

    if (
      importValue.targetIsMulti &&
      importValue.targetFieldId &&
      field &&
      !field.settings.multi
    ) {
      const change = {
        type: DatabaseChangeType.SET_IS_MULTI,
        fieldId: importValue.targetFieldId,
        isMulti: true,
      } as DatabaseChange;

      fieldSettingMultiChanges.push(change);
    }
  }

  return fieldSettingMultiChanges;
};

/**
 * Determines the last field id that was updated by the changeset
 */
const getLastUpdatedFieldId = (
  createFieldsChangeset: DatabaseChange[],
  setValuesChangeset: DatabaseChange[]
) => {
  const lastNewFieldId = getLastFieldIdByChangeType(
    createFieldsChangeset,
    DatabaseChangeType.ADD_FIELD
  );
  const lastExistingFieldId = getLastFieldIdByChangeType(
    setValuesChangeset,
    DatabaseChangeType.SET_VALUE
  );

  return lastNewFieldId || lastExistingFieldId;
};

/**
 * Determines the last entry id that was updated by the changeset
 */
const getLastUpdatedEntryId = (setValuesChangeset: DatabaseChange[]) => {
  const lastNewEntryId = getLastEntryIdByChangeType(
    setValuesChangeset,
    DatabaseChangeType.ADD_ENTRY
  );
  const lastExistingEntryId = getLastEntryIdByChangeType(
    setValuesChangeset,
    DatabaseChangeType.SET_VALUE
  );

  return lastNewEntryId || lastExistingEntryId;
};

const doSetSoftSelectionOnPastedContent =
  ({
    changesets,
    settings,
    contentId,
  }: {
    changesets: Record<string, DatabaseChange[]>;
    settings: ImportSettings;
    contentId: string;
  }): Dispatcher<void> =>
  (dispatch) => {
    const { createFieldsChangeset, setValuesChangeset } = changesets;

    const lastUpdatedFieldId = getLastUpdatedFieldId(
      createFieldsChangeset,
      setValuesChangeset
    );
    const lastUpdatedEntryId = getLastUpdatedEntryId(setValuesChangeset);

    if (lastUpdatedFieldId && lastUpdatedEntryId) {
      const firstFieldInSelection = settings.fieldReferences[0];
      const firstEntryInSelection = settings.entryReferences[0];
      if (firstFieldInSelection && firstEntryInSelection) {
        dispatch(deselectAll());
        if (
          firstFieldInSelection.type === ImportFieldMode.EXISTING &&
          firstEntryInSelection.type === ImportEntryMode.EXISTING
        ) {
          dispatch(
            selectValueSingle({
              fieldId: firstFieldInSelection.fieldId,
              entryId: firstEntryInSelection.entryId,
            })
          );
        } else if (
          firstFieldInSelection.type === ImportFieldMode.CREATE &&
          firstEntryInSelection.type === ImportEntryMode.CREATE
        ) {
          dispatch(
            selectValueSingle({
              fieldId: firstFieldInSelection.field.id,
              entryId: firstEntryInSelection.entry.id,
            })
          );
        }
        dispatch(setSoftSelection({ value: true }));
        dispatch(
          selectValueRange({
            contentId,
            fieldId: lastUpdatedFieldId,
            entryId: lastUpdatedEntryId,
          })
        );
      }
    }
  };

/**
 * Determines if the content pasted has been filtered out by the current view.
 */
export function getFilteredOutEntriesCount({
  numberOfEntriesPasted,
  changeset,
  viewSettings,
  database,
  state,
}: {
  numberOfEntriesPasted: number;
  changeset: DatabaseChange[];
  viewSettings: DatabaseViewSettings;
  database: Database;
  state: DefaultRootState;
}): number {
  const numberOfEntriesAdded = changeset
    .filter((change) => change.type === DatabaseChangeType.ADD_ENTRY)
    .filter(
      (change) =>
        !viewSettings.filter ||
        matchesFilter(
          {
            database,
            entry: change.entry,
          },
          viewSettings.filter,
          state
        )
    ).length;

  const numberOfEntriesReplaced = changeset
    .filter((change) => change.type === DatabaseChangeType.SET_VALUE)
    .filter((change) => {
      const entry = getDatabaseEntryOptional(database, change.entryId);

      if (!viewSettings.filter || !entry) {
        return true;
      }

      // since the entry’s value has not yet been updated, we update it so
      // that it can be passed into matchesFilter
      const newEntry = {
        ...entry,
        values: {
          ...entry.values,
          [change.fieldId]: change.value,
        },
      };
      return matchesFilter({ database, entry: newEntry }, viewSettings.filter, state);
    }).length;

  return numberOfEntriesPasted - (numberOfEntriesReplaced + numberOfEntriesAdded);
}

/**
 * Filters out the set values changesets that are for new fields.
 */
const filterNewFieldSetValueChanges = (
  createFieldsChangeset: DatabaseChange[],
  setValuesChangeset: DatabaseChange[]
) => {
  const idsOfFieldsToBeCreated = getIdsOfFieldsToBeCreated(createFieldsChangeset);
  return setValuesChangeset.filter((change) => {
    if ('fieldId' in change) {
      return !idsOfFieldsToBeCreated.includes(change.fieldId);
    }
    return true;
  });
};

function getIdsOfFieldsToBeCreated(createFieldsChangeset: DatabaseChange[]) {
  return createFieldsChangeset
    .filter((change) => change.type === DatabaseChangeType.ADD_FIELD)
    .map((change) => change.field.id);
}

/**
 * This function will take the createFieldsChangeset and setValuesChangeset and convert them into a
 * createFieldAndSetValueChangeset. This new change set will handle setting the values for both existing and
 * non-existing fields and entries. To makes these changes, this is the only changeset that needs to be dispatched.
 *
 * @param createFieldsChangeset
 * @param setValuesChangeset
 * @return createFieldAndSetValueChangeset
 */
const getCreateFieldAndSetValueChangeset = (
  createFieldsChangeset: DatabaseChange[],
  setValuesChangeset: DatabaseChange[]
): DatabaseChange[] => {
  const result: DatabaseChange[] = [];
  const idsOfFieldsToBeCreated = getIdsOfFieldsToBeCreated(createFieldsChangeset);
  // in this case, there are no fields that need to be created so just return the setValueChangeset untouched
  if (idsOfFieldsToBeCreated.length === 0) {
    return [];
  }

  // for ADD_FIELD change, there will be a corresponding SET_VALUE change. This loop will find this pair, and create
  // a new CREATE_FIELD_AND_SET_VALUE change.
  for (const fieldId of idsOfFieldsToBeCreated) {
    const createFieldChange = createFieldsChangeset.find((change) =>
      change.type === DatabaseChangeType.ADD_FIELD ? change.field.id === fieldId : false
    );
    const setValueChange = deleteFromArray(
      setValuesChangeset,
      (change) =>
        change.type === DatabaseChangeType.SET_VALUE && change.fieldId === fieldId
    );

    if (
      createFieldChange?.type === DatabaseChangeType.ADD_FIELD &&
      setValueChange?.type === DatabaseChangeType.SET_VALUE
    ) {
      result.push({
        entryId: setValueChange.entryId,
        value: setValueChange.value,
        change: setValueChange.change,
        field: createFieldChange.field,
        currentViewId: createFieldChange?.currentViewId,
        index: createFieldChange?.index,
        type: DatabaseChangeType.CREATE_FIELD_AND_SET_VALUE,
      });
    }
  }

  return result;
};

/**
 * Saves the pasted content to the database draft.
 **/
const doSavePastedContent =
  (
    contentId: string,
    scope: string,
    parsedFile: ImportResult,
    settings: ImportSettings,
    currentViewId: string | undefined,
    showFlags: ShowPasteFlag,
    createMentionNotification: CreateMentionNotificationType,
    viewSettings: DatabaseViewSettings
  ): Dispatcher<Promise<void> | { tag: 'UNKNOWN_FILE_TYPE'; fileType: string }> =>
  async (dispatch, getState) => {
    const state = getState();
    const database = selectDatabaseByContentIdRequired(getState(), { contentId });
    const cols = parsedFile.rows.map((row) => row.cols);
    const importValues: ImportValues[] = Array.from(cols).flat();

    const tagsToCreate: Record<string, TagCreationData> = getTagsToCreate(
      importValues,
      database
    );

    const { allowTagCreationChanges, newTagOptionChanges } = getCreateNewTagChanges(
      tagsToCreate,
      database,
      importValues
    );

    await createMentionsNotificationOnPaste(
      importValues,
      database,
      createMentionNotification
    );

    // create and dispatch changesets
    const databaseDraft = selectDatabaseDraftByContentId(state, {
      contentId,
      scope,
      includeDeleted: true,
    });

    const changes = createRangedChanges(
      settings,
      parsedFile,
      databaseDraft,
      {
        dispatch,
        getState,
      },
      currentViewId
    );

    const draftedChangesets = selectDraftedChangeset(state, { contentId, scope });
    const createFieldsChangeset = addChangeToChangeset(
      draftedChangesets,
      ...changes.filter((change) => change.type === DatabaseChangeType.ADD_FIELD)
    );
    let setValuesChangeset = addChangeToChangeset(
      draftedChangesets,
      ...changes.filter((change) => change.type !== DatabaseChangeType.ADD_FIELD)
    );

    const createFieldAndSetValueChangeset = getCreateFieldAndSetValueChangeset(
      createFieldsChangeset,
      setValuesChangeset
    );

    let multiChangeset = addChangeToChangeset(
      draftedChangesets,
      ...getFieldSettingMultiChanges(importValues, database)
    );
    let newTagChangeset = addChangeToChangeset(draftedChangesets, ...newTagOptionChanges);
    let allowTagCreationChangeset = addChangeToChangeset(
      draftedChangesets,
      ...allowTagCreationChanges
    );

    // Changesets to evaluate on lock structure
    const preventChangesOnLock = [
      ...createFieldsChangeset,
      ...multiChangeset,
      ...newTagChangeset,
      ...allowTagCreationChangeset,
    ];

    const isPartialPaste =
      Boolean(database.meta.isLocked) && Boolean(preventChangesOnLock.length);

    if (isPartialPaste) {
      setValuesChangeset = getNewSetValuesChangesets({
        preventChangesOnLock,
        setValuesChangeset,
      });
      multiChangeset = [];
      newTagChangeset = [];
      allowTagCreationChangeset = [];
    }

    const numberOfEntriesPasted = parsedFile.rows.length;
    const numberOfEntriesFiltered = getFilteredOutEntriesCount({
      numberOfEntriesPasted,
      changeset: setValuesChangeset,
      viewSettings,
      database,
      state,
    });

    // all changesets should be dispatched at the same time to have a consistent database state
    batch(() => {
      if (!database.meta.isLocked) {
        collabDatabaseBatch({ dispatch, getState }, contentId, () => {
          dispatch(setDraftedChangeset(contentId, scope, multiChangeset));
          dispatch(setDraftedChangeset(contentId, scope, newTagChangeset));
          dispatch(setDraftedChangeset(contentId, scope, allowTagCreationChangeset));
          dispatch(
            setDraftedChangeset(contentId, scope, createFieldAndSetValueChangeset)
          );
          dispatch(
            setDraftedChangeset(
              contentId,
              scope,
              filterNewFieldSetValueChanges(createFieldsChangeset, setValuesChangeset)
            )
          );
        });
      } else {
        collabDatabaseBatch({ dispatch, getState }, contentId, () => {
          dispatch(setDraftedChangeset(contentId, scope, setValuesChangeset));
        });
      }
    });

    const isPastingOverContent = isPastingOverExistingContent(
      setValuesChangeset,
      database,
      state
    );

    const shouldFieldSettingUpdate =
      !database.meta.isLocked &&
      (shouldUpdateMultiSetting(importValues, database) ||
        Boolean(allowTagCreationChanges.length));

    const isMismatchedPasteContent = !doesPasteContentTypeMatchField(
      importValues,
      database,
      setValuesChangeset,
      state
    );

    renderPasteFlags(
      {
        isPartialPaste,
        isPastingOverContent,
        isMismatchedPasteContent,
        shouldFieldSettingUpdate,
      },
      numberOfEntriesFiltered,
      numberOfEntriesPasted,
      showFlags
    );

    dispatch(
      doSetSoftSelectionOnPastedContent({
        changesets: {
          createFieldsChangeset: [
            ...createFieldsChangeset,
            ...createFieldAndSetValueChangeset,
          ],
          setValuesChangeset: [...setValuesChangeset, ...createFieldAndSetValueChangeset],
        },
        settings,
        contentId,
      })
    );
  };

export const doProcessPastedContent =
  (
    contentId: string,
    items: DataTransferItemList,
    scope: string,
    currentViewId: string,
    defaultFieldLabel: string,
    showFlags: ShowPasteFlag,
    createMentionNotification: CreateMentionNotificationType,
    localViewSettings: DatabaseViewSettings
  ): Dispatcher<Promise<void>> =>
  async (dispatch, getState) => {
    if (items.length === 0) {
      return;
    }

    const state = getState();
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const hasSelectedCells = selectHasSelectedCells(state, {
      contentId,
    });

    if (!hasSelectedCells) {
      return;
    }

    // when pasting images into attachment fields, return early
    // because image handling is done in AttachmentValueEdit.tsx
    const isOnlySelectingAttachments = selectedFieldsMatchType(
      database,
      state,
      DatabaseFieldType.ATTACHMENT
    );
    const imageItems = getImageItems(items);
    if (imageItems.length && isOnlySelectingAttachments) {
      return;
    }

    const pasteSupportedItems = await readPasteContent(items);
    if (!pasteSupportedItems.success) {
      return handleReadPasteError(pasteSupportedItems, showFlags);
    }

    const convertedPasteContent = await convertToHtmlFormat(
      database.name,
      state,
      pasteSupportedItems.items
    );
    const pasteContent = parsePasteContent(convertedPasteContent);
    if (!pasteContent.success) {
      handleParsePasteError(showFlags);
      return;
    }

    const selectedFieldIds = selectSelectedFieldIds(state, {
      contentId: database.contentId,
    });
    const selectedEntryIds = selectSelectedEntryIds(state, {
      contentId: database.contentId,
    });

    const visibleEntries = applyDatabaseView(
      database,
      database,
      localViewSettings,
      getState()
    ).entries;

    const visibleFields = localViewSettings.fields
      ? getVisibleFields(database, localViewSettings)
      : database.fields;
    const settings = getImportSettingsFromSelection(
      selectedFieldIds,
      selectedEntryIds,
      visibleFields,
      visibleEntries
    );

    const importResult = applyImportSettings(
      database,
      pasteContent.file,
      settings,
      getState()
    );
    if (!importResult.success) {
      return handleApplyImportSettingsError(importResult);
    }

    const settingsWithMissingItems = getImportSettingsWithMissingItems(
      selectedFieldIds,
      selectedEntryIds,
      visibleFields,
      visibleEntries,
      database,
      importResult.result,
      defaultFieldLabel
    );

    dispatch(
      doSavePastedContent(
        contentId,
        scope,
        importResult.result,
        settingsWithMissingItems,
        currentViewId,
        showFlags,
        createMentionNotification,
        localViewSettings
      )
    );

    pasteContentExperience.success();
  };

export const doSaveImport =
  (
    contentId: string,
    parentScope: string,
    scope: string,
    importedFile: ParsedImportFile,
    settings: ImportSettings,
    currentViewId: string
  ): Dispatcher<void | {
    tag: 'UNKNOWN_FILE_TYPE';
    fileType: string;
  }> =>
  (dispatch, getState) => {
    const state = getState();
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const parsed = applyImportSettings(database, importedFile, settings, state);
    if (!parsed.success) {
      return parsed.error;
    }

    const newFieldsChangeset = settings.fieldReferences.flatMap(
      (ref): DatabaseChange[] => {
        if (ref?.type === ImportFieldMode.CREATE) {
          return [
            {
              type: DatabaseChangeType.ADD_FIELD,
              field: ref.field,
              currentViewId,
            },
          ];
        } else {
          return [];
        }
      }
    );

    const newEntriesChangeset = parsed.result.rows.flatMap((row): DatabaseChange[] =>
      row.isSelected
        ? [
            {
              type: DatabaseChangeType.ADD_ENTRY,
              entry: createEntry(database, undefined, {
                values: Object.fromEntries(
                  row.cols.flatMap((cell, idx) => {
                    const field = resolveFieldReference(
                      database,
                      settings.fieldReferences[idx]
                    );
                    return field && cell.value ? [[field.id, cell.value]] : [];
                  })
                ),
              }),
            },
          ]
        : []
    );

    const changeset = addChangeToChangeset(
      selectDraftedChangeset(state, { contentId, scope }),
      ...newFieldsChangeset,
      ...newEntriesChangeset
    );

    dispatch(setDraftedChangeset(contentId, parentScope, changeset));
  };
